<template>
  <b-row class="app-section">
    <b-col>
      <b-row style="padding: 2rem">
        <b-col class="text-center">
          <b-btn
            id="v-step-2"
            :to="{ path: `/plot/${nextChart.i}`, query: nextChart.query }"
            variant="primary"
            size="lg"
          >
            <i class="fas fa-plus"></i>
            Add New Plot
          </b-btn>
          <!--Opens map on new tab per user feedback-->
          <b-btn
            id="v-step-3"
            variant="primary"
            size="lg"
            :to="{ path: '/map' }"
            target="_blank"
          >
            <i class="fas fa-globe-americas"></i>
            Explore Map
          </b-btn>
        </b-col>
      </b-row>
      <b-row v-if="this.chartsLength === 0">
        <b-col class="text-center">
          <b-icon
            icon="arrow-up"
            animation="cylon-vertical"
            font-scale="4"
          ></b-icon>
          <p class="mt-4" style="font-size: 1.2rem; font-weight: bold">
            You have not yet created any plots. <br />
            Why don't you create one or explore the map with the buttons
            above.
            <br>
            <b-img-lazy
              src="https://s3.us-west-2.amazonaws.com/media.ooica.net/wp-content/uploads/2019/01/03192643/octopus_r1617_00167-cropped.jpg"
              alt="Graneledone Octopus"
              height="400"
              class="mt-2"
            ></b-img-lazy>
            <br>
            <small>
              Graneledone Octopus on the rocks, VISIONS '13, Leg 3.
              Photo credit: OOI-NSF/UW/CSSF
            </small>
          </p>
        </b-col>
      </b-row>
      <b-row v-if="this.chartsLength > 0">
        <b-col>
          <grid-layout
            :layout="charts"
            :col-num="12"
            :row-height="30"
            :is-draggable="draggable"
            :is-resizable="resizable"
            :is-mirrored="false"
            :responsive="true"
            :vertical-compact="true"
            :margin="[10, 10]"
            :use-css-transforms="true"
          >
            <grid-item
              id="v-step-1"
              class="card v-shadow"
              v-for="chart in charts"
              :x="chart.x"
              :y="chart.y"
              :w="chart.w"
              :h="chart.h"
              :i="chart.i"
              :key="chart.i"
              dragAllowFrom=".chart-header"
              @moved="movedEvent"
              @resized="resizedEvent"
            >
              <plot-container :chart="chart"></plot-container>
            </grid-item>
          </grid-layout>
        </b-col>
      </b-row>
    </b-col>
    <!-- MOUNT TOUR | name can be used anywhere to start the tour -->
    <v-tour :name="tourName" :steps="steps"></v-tour>
  </b-row>
</template>

<script>
import { GridLayout, GridItem } from 'vue-grid-layout';
import { mapActions, mapGetters } from 'vuex';
import getNewChart from '@/utils/chartTools';
import PlotContainerVue from './PlotContainer';

export default {
  data() {
    return {
      draggable: true,
      resizable: true,
      nextChart: { i: null, query: null },
      // Configuration for tour
      tourName: 'vizTour',
      steps: [
        {
          target: '#v-step-0',
          content:
            'Navigate through different applications by using the navbar, including the map, data visualization tools, and Jupyter notebooks.',
          params: {
            highlight: false,
          },
        },
        {
          target: '#v-step-1',
          content:
            'This is a plot container, it holds your desired plot. You can hold and drag on the top header to move it around.',
          params: {
            highlight: false,
            placement: 'right',
          },
        },
        {
          target: '#v-step-2',
          content: 'Click to add new plot containers.',
          params: {
            highlight: true,
          },
        },
        {
          target: '#v-step-3',
          content: 'Click to go back to map interface to explore.',
          params: {
            highlight: true,
          },
        },
        {
          target: '#v-step-4',
          content:
            'Click here to add/edit datasets used in the plot, or to make style changes',
          params: {
            highlight: true,
          },
        },
        {
          target: '#v-step-5',
          content:
            'Click to remove remove plot container from the set of plots.',
          params: {
            highlight: true,
          },
        },
      ],
    };
  },
  created() {
    if (this.chartsLength === 0) {
      // Only create chart when there are 0
      this.createChart();
    }
  },
  mounted() {
    if (!localStorage.getItem(this.tourName)) {
      // Access the desired tour
      this.$tours[this.tourName].start();
      localStorage.setItem(this.tourName, true);
    }
    this.nextChart = getNewChart(this.charts);
  },
  components: {
    'grid-layout': GridLayout,
    'grid-item': GridItem,
    'plot-container': PlotContainerVue,
  },
  computed: {
    ...mapGetters('visualization/charts', {
      charts: 'list',
      chartsLength: 'count',
    }),
  },
  methods: {
    ...mapActions('visualization/charts', [
      'addChart',
      'updateChartPanelPos',
      'updateChartPanelSize',
    ]),
    /**
     * Creates a new plot and then
     * navigate to that plot details page.
     */
    createChart() {
      const newChart = getNewChart(this.charts);
      this.addChart({ chart: newChart });
      // Add new plot button auto push to newChart details page.
      this.$router.push({
        path: `/plot/${newChart.i}`,
        query: newChart.query,
      });
    },
    /**
     * Captures chart panel move event
     * @param {*} uuid - chart uuid
     * @param {*} x - chart x position
     * @param {*} y - chart y position
     */
    movedEvent(uuid, x, y) {
      this.updateChartPanelPos({ uuid, x, y });
    },
    /**
     * Captures chart panel resize event
     * @param {*} uuid - chart uuid
     * @param {*} h - chart height
     * @param {*} w - chart width
     */
    resizedEvent(uuid, h, w) {
      this.updateChartPanelSize({ uuid, w, h });
    },
  },
};
</script>

<style lang="css">
.viz-app {
  height: 100%;
  width: 100%;
}
.v-shadow {
  -webkit-box-shadow: 0 8px 10px 1px rgba(0, 0, 0, 0.14),
    0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 5px 5px -3px rgba(0, 0, 0, 0.2);
  -moz-box-shadow: 0 8px 10px 1px rgba(0, 0, 0, 0.14),
    0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 5px 5px -3px rgba(0, 0, 0, 0.2);
  box-shadow: 0 8px 10px 1px rgba(0, 0, 0, 0.14),
    0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 5px 5px -3px rgba(0, 0, 0, 0.2);
}
</style>
